
import { Ball, SimulationConfig, Vector2D, MorphData, CollisionEvent, BlastEvent } from '../types';
import { AUDIO } from '../services/AudioService';
import { ShapeUtils } from './physics/ShapeUtils';
import { BasicModesLogic } from './modes/BasicModesLogic';
import { SpaceSpiralLogic } from './modes/SpaceSpiralLogic';
import { EscapeLogic } from './modes/EscapeLogic';
import { TrailLogic } from './modes/TrailLogic';
import { resolveWallCollision, resolveOutsideColliderCollision } from './physics/BoundaryCollision';

export class LoopPhysicsLogic {
  public static handleSubsteps(
    deltaTime: number,
    effectiveCfg: SimulationConfig,
    balls: Ball[],
    centerX: number,
    centerY: number,
    rotRad: number,
    hue: number,
    now: number,
    currentTS: number,
    morphData: MorphData,
    collisionEvents: CollisionEvent[],
    persistentSpectrumBoost: Float32Array,
    persistentSpectrumTarget: Float32Array,
    containerRadiusRef: any,
    ballGrowthRateRef: any,
    containerShrinkRateRef: any,
    timeScaleRef: any,
    speedIncreaseRateRef: any,
    shakeRef: any,
    counterPopRef: any,
    setBounceCount: any,
    setIsPlaying: any,
    initSimulation: any,
    triggerBlast: any,
    playCollisionSound: any,
    escapeRingsRef: any,
    isRotationStopped: boolean,
    hyperSpeedState: any,
    timeRemainingRef: any,
    escapeAccelerationBoostRef: any,
    physicsSystem: any,
    ballPool: any,
    trailCircles: any[],
    trailCirclePool: any,
    workerRef: any
  ) {
    const substeps = effectiveCfg.physicsSubsteps;
    const physicsDt = Math.min(deltaTime / 1000, 0.05);
    const spawns: {pos: Vector2D, color: string}[] = [];

    for (let s = 0; s < substeps; s++) {
      const stepTS = currentTS / substeps;
      for (let i = 0; i < balls.length; i++) {
        const b = balls[i];
        
        if (effectiveCfg.mode === 'SPACE_SPIRAL' && i > 0) {
            SpaceSpiralLogic.updateBox(b, effectiveCfg, now, i);
        } else {
            BasicModesLogic.updateBall(b, effectiveCfg, now, hue, i, stepTS);
        }

        if (s === 0) {
          if (!b.trail) b.trail = []; 
          if (effectiveCfg.mode === 'TRAIL' || effectiveCfg.trailLength > 0) {
             b.trail.push({ ...b.pos }); 
             if (b.trail.length > effectiveCfg.trailLength) b.trail.shift();
          }
        }
        
        if (effectiveCfg.mode === 'TRAIL' || effectiveCfg.trailLength > 0 || effectiveCfg.trailPersistence) {
            TrailLogic.updateTrail(b, effectiveCfg, deltaTime, substeps, currentTS, trailCircles, hue, trailCirclePool);
        }

        if (effectiveCfg.mode !== 'SPAWNER') {
            let applyGravity = true;
            if (effectiveCfg.mode === 'SPACE_SPIRAL') {
                if (i === 0) {
                    applyGravity = effectiveCfg.spaceSpiralGravity;
                    if (applyGravity) {
                        b.vel.y += (effectiveCfg.gravity * (effectiveCfg.spaceSpiralGravityPower || 1.0)) * stepTS;
                        applyGravity = false; 
                    }
                    if (effectiveCfg.spaceSpiralForwardThrust && effectiveCfg.spaceSpiralForwardThrust > 0) {
                         const thrustDuration = effectiveCfg.spaceSpiralThrustDuration || 0;
                         if (!b.spawnTime) b.spawnTime = now;
                         if (now - b.spawnTime < thrustDuration) {
                             let vx = 0, vy = 0;
                             if (effectiveCfg.spaceSpiralThrustAngle !== undefined) {
                                 const angleRad = (effectiveCfg.spaceSpiralThrustAngle * Math.PI) / 180;
                                 vx = Math.cos(angleRad); vy = Math.sin(angleRad);
                             } else { vx = 0.707; vy = 0.707; }
                             b.vel.x += vx * effectiveCfg.spaceSpiralForwardThrust * stepTS;
                             b.vel.y += vy * effectiveCfg.spaceSpiralForwardThrust * stepTS;
                         }
                    }
                } else applyGravity = false;
            }
            if (applyGravity) b.vel.y += effectiveCfg.gravity * stepTS; 
            
            const airRes = (effectiveCfg.mode === 'SPACE_SPIRAL') ? (i === 0 ? effectiveCfg.spaceSpiralAirResistance : effectiveCfg.spaceSpiralBoxAirResistance) : effectiveCfg.airResistance;
            b.vel.x *= Math.pow(airRes, stepTS); 
            b.vel.y *= Math.pow(airRes, stepTS);
            b.pos.x += b.vel.x * stepTS; 
            b.pos.y += b.vel.y * stepTS;

            if (effectiveCfg.mode !== 'ESCAPE') resolveOutsideColliderCollision(b, centerX, centerY, effectiveCfg);
        }
        
        if (effectiveCfg.mode === 'ESCAPE') {
            EscapeLogic.handleBallCollision(b, escapeRingsRef, effectiveCfg, centerX, centerY, stepTS, morphData, rotRad, (ring, originalIndex) => {
                triggerBlast({ pos: { x: 0, y: 0 }, color: `hsl(${(hue + ring.hue) % 360}, 100%, 50%)`, radius: b.radius }, ring.radius, ring.angle, morphData);
                if (effectiveCfg.escapeTimeChallengeMode) timeRemainingRef.current += Math.max(0, effectiveCfg.escapeTimeAddition * 0.5);
                if (effectiveCfg.escapeAccelerationEnabled) escapeAccelerationBoostRef.current = Math.min(effectiveCfg.escapeAccelerationMax, escapeAccelerationBoostRef.current + effectiveCfg.escapeAccelerationMultiplier);
                setBounceCount((prev: number) => prev + 1);
                counterPopRef.current = 1.35;
                if (escapeRingsRef.current.length === 0) setTimeout(() => initSimulation(), 2000);
            }, playCollisionSound, (intensity) => { shakeRef.current = Math.max(shakeRef.current, intensity); }, (pos, radius, color) => {
                this.boostSpectrum(pos, effectiveCfg, persistentSpectrumTarget, 1.0);
                collisionEvents.push({ id: Math.random().toString(), pos: { ...pos }, startTime: now, radius: radius * 2.5 * (effectiveCfg.collisionEffectScale || 1.0), color: color, effectType: effectiveCfg.collisionEffect, duration: effectiveCfg.collisionEffectDuration || 800 });
            });
        } else if (effectiveCfg.mode !== 'SPAWNER') {
            const isTouching = resolveWallCollision(b, centerX, centerY, containerRadiusRef.current, effectiveCfg, morphData, false, (pos, radius, color) => {
                this.boostSpectrum(pos, effectiveCfg, persistentSpectrumTarget, 1.0);
                collisionEvents.push({ id: Math.random().toString(), pos: { ...pos }, startTime: now, radius: radius * 2.5 * (effectiveCfg.collisionEffectScale || 1.0), color: color, effectType: effectiveCfg.collisionEffect, duration: effectiveCfg.collisionEffectDuration || 800 });
            });
            if (isTouching > 0.15) { 
               playCollisionSound(); 
               shakeRef.current = Math.max(shakeRef.current, 2 * effectiveCfg.shakeIntensityMultiplier);
               if (effectiveCfg.speedIncreaseMode === 'COLLISION') {
                   timeScaleRef.current += speedIncreaseRateRef.current;
                   if (effectiveCfg.speedAcceleration !== 0) speedIncreaseRateRef.current += effectiveCfg.speedAcceleration;
               }
               if (effectiveCfg.ballGrowthMode === 'COLLISION') {
                   this.handleGrowthCollision(b, effectiveCfg, morphData, containerRadiusRef, ballGrowthRateRef, persistentSpectrumBoost, centerX, centerY, triggerBlast, setIsPlaying, initSimulation);
               }
               if (effectiveCfg.containerShrinkMode === 'COLLISION') {
                   containerRadiusRef.current = Math.max(effectiveCfg.minContainerRadius, containerRadiusRef.current - containerShrinkRateRef.current);
                   if (effectiveCfg.containerShrinkAcceleration !== 0) containerShrinkRateRef.current += effectiveCfg.containerShrinkAcceleration;
               }
               setBounceCount((prev: number) => prev + 1);
               if (effectiveCfg.mode === 'DRAWER') {
                  b.hitPoints = b.hitPoints || []; 
                  const angle = Math.atan2(b.pos.y-centerY, b.pos.x-centerX);
                  const currentShape = morphData ? morphData.from : (effectiveCfg.containerShape || 'CIRCLE');
                  const shapeR = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, currentShape, morphData.to, morphData.factor, containerRadiusRef.current);
                  b.hitPoints.push({ x: centerX + Math.cos(angle)*shapeR, y: centerY + Math.sin(angle)*shapeR });
               }
               collisionEvents.push({ id: Math.random().toString(), pos: { ...b.pos }, startTime: now, radius: b.radius * 2.5, color: b.color, effectType: effectiveCfg.collisionEffect, duration: effectiveCfg.collisionEffectDuration || 800 });
            } else if (effectiveCfg.audioSlidingEnabled && isTouching > 0) { 
                AUDIO.playSlidingSound(Math.sqrt(b.vel.x**2 + b.vel.y**2), effectiveCfg);
            }
        }
        this.clampVelocity(b, effectiveCfg);
      }
      
      if (effectiveCfg.mode === 'SPAWNER') {
        if (workerRef.current) workerRef.current.postMessage({ type: 'STEP', payload: { dt: physicsDt, containerRadius: containerRadiusRef.current } });
      } else if (effectiveCfg.mode !== 'ESCAPE') { 
          physicsSystem.resolveBallCollisions(balls, effectiveCfg, (b1: Ball, b2: Ball, impact: number) => {
            if (impact > 0.15) {
              if (effectiveCfg.mode === 'SPACE_SPIRAL') {
                  b1.isDebris = true; b2.isDebris = true; setBounceCount((prev: number) => prev + 1);
                  if (effectiveCfg.spaceSpiralSoundBounce) AUDIO.playSoundEffect(effectiveCfg.spaceSpiralSoundBounce, effectiveCfg);
              } else playCollisionSound(); 
              
              if (effectiveCfg.speedIncreaseMode === 'COLLISION') {
                  timeScaleRef.current += speedIncreaseRateRef.current;
                  if (effectiveCfg.speedAcceleration !== 0) speedIncreaseRateRef.current += effectiveCfg.speedAcceleration;
              }
              
              if (effectiveCfg.ballGrowthMode === 'COLLISION') {
                  this.handleBallBallGrowth(b1, b2, effectiveCfg, morphData, containerRadiusRef, ballGrowthRateRef, persistentSpectrumBoost);
              }

              if (effectiveCfg.containerShrinkMode === 'COLLISION') {
                  containerRadiusRef.current = Math.max(effectiveCfg.minContainerRadius, containerRadiusRef.current - containerShrinkRateRef.current);
                  if (effectiveCfg.containerShrinkAcceleration !== 0) containerShrinkRateRef.current += effectiveCfg.containerShrinkAcceleration;
              }
              
              const canSpawn = effectiveCfg.spawnOnBallCollision && balls.length < effectiveCfg.maxBalls && effectiveCfg.mode !== 'SPACE_SPIRAL';
              if (canSpawn && now - (b1.lastSpawnTime || 0) > 150 && now - (b2.lastSpawnTime || 0) > 150) {
                  spawns.push({ pos: { x: (b1.pos.x + b2.pos.x)/2, y: (b1.pos.y + b2.pos.y)/2 }, color: b1.color }); 
                  b1.lastSpawnTime = now; b2.lastSpawnTime = now;
              }
            }
        });
      }
    }
    return spawns;
  }

  private static boostSpectrum(pos: Vector2D, config: SimulationConfig, target: Float32Array, intensityMult: number) {
      if (!config.rhythmicSpectrumEnabled) return;
      const angle = Math.atan2(pos.y, pos.x);
      const normalizedAngle = (angle + Math.PI) / (2 * Math.PI);
      const boostIndex = Math.floor(normalizedAngle * 256);
      const spread = config.rhythmicSpectrumCollisionSpread ?? 12;
      const intensity = config.rhythmicSpectrumCollisionIntensity ?? 1.0;
      for (let j = -spread; j <= spread; j++) {
          const idx = (boostIndex + j + 256) % 256;
          const falloff = 1 - Math.abs(j) / (spread + 1);
          target[idx] = Math.max(target[idx], 1.8 * intensity * falloff * intensityMult);
      }
  }

  private static handleGrowthCollision(b: Ball, effectiveCfg: SimulationConfig, morphData: MorphData, containerRadiusRef: any, ballGrowthRateRef: any, spectrum: Float32Array, centerX: number, centerY: number, triggerBlast: any, setIsPlaying: any, initSimulation: any) {
    const minContainerR = ShapeUtils.getMinRadius(morphData?.from || effectiveCfg.containerShape, morphData?.to || effectiveCfg.containerShape, morphData?.factor || 0, containerRadiusRef.current, effectiveCfg.containerRadius) - (effectiveCfg.containerLineWidth || 4) / 2;
    const maxAllowed = Math.min(effectiveCfg.ballMaxRadius, minContainerR);
    const ballHash = b.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const preferredBinOffset = (ballHash % 64) - 32;
    const ballAngle = Math.atan2(b.pos.y - centerY, b.pos.x - centerX);
    const baseBinIdx = Math.floor(((ballAngle + Math.PI) / (2 * Math.PI)) * 256);
    const binIdx = (baseBinIdx + preferredBinOffset + 256) % 256;
    const localIntensity = spectrum[binIdx];
    let globalIntensity = 0;
    for (let j = 0; j < 256; j += 16) globalIntensity += spectrum[j];
    globalIntensity /= 16;
    const variation = 0.7 + (ballHash % 60) / 100;
    const musicMultiplier = 1 + (localIntensity * 2.0 + globalIntensity * 0.5) * variation;
    const growth = ballGrowthRateRef.current * musicMultiplier;
    const nextRadius = b.radius + growth;

    if (effectiveCfg.ballGrowthAcceleration !== 0) ballGrowthRateRef.current += effectiveCfg.ballGrowthAcceleration;

    if (effectiveCfg.mode === 'TRAIL' && effectiveCfg.trailEnableBorderBreak && b.radius < maxAllowed && nextRadius >= maxAllowed) {
        triggerBlast({ pos: { x: centerX, y: centerY }, color: b.color, radius: b.radius }, containerRadiusRef.current, undefined, morphData, 'PHYSICS_SHATTER');
        setIsPlaying(false);
        setTimeout(() => { initSimulation(true); setIsPlaying(true); }, 2000);
    } else {
        const actualGrowth = Math.min(maxAllowed, nextRadius) - b.radius;
        b.radius += actualGrowth;
        const relX = b.pos.x - centerX, relY = b.pos.y - centerY;
        const dist = Math.sqrt(relX * relX + relY * relY);
        if (dist > 0.1) {
            b.pos.x -= (relX / dist) * actualGrowth;
            b.pos.y -= (relY / dist) * actualGrowth;
        }
    }
  }

  private static handleBallBallGrowth(b1: Ball, b2: Ball, effectiveCfg: SimulationConfig, morphData: MorphData, containerRadiusRef: any, ballGrowthRateRef: any, spectrum: Float32Array) {
      const minContainerR = ShapeUtils.getMinRadius(morphData?.from || effectiveCfg.containerShape, morphData?.to || effectiveCfg.containerShape, morphData?.factor || 0, containerRadiusRef.current, effectiveCfg.containerRadius) - (effectiveCfg.containerLineWidth || 4) / 2;
      let globalIntensity = 0;
      for (let j = 0; j < 256; j += 8) globalIntensity += spectrum[j];
      globalIntensity /= 32;
      const growthBase = ballGrowthRateRef.current;
      
      [b1, b2].forEach(b => {
          const ballAngle = Math.atan2(b.pos.y, b.pos.x);
          const binIdx = Math.floor(((ballAngle + Math.PI) / (2 * Math.PI)) * 256) % 256;
          const ballHash = b.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
          const variation = 0.9 + (ballHash % 20) / 100;
          const musicMultiplier = 1 + (spectrum[binIdx] * 1.0 + globalIntensity * 0.5) * variation;
          const maxAllowed = Math.min(effectiveCfg.ballMaxRadius, minContainerR);
          b.radius = Math.min(maxAllowed, b.radius + growthBase * musicMultiplier);
      });

      const dx = b2.pos.x - b1.pos.x, dy = b2.pos.y - b1.pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 0.1) {
          const nx = dx / dist, ny = dy / dist;
          b1.pos.x -= nx * growthBase; b1.pos.y -= ny * growthBase;
          b2.pos.x += nx * growthBase; b2.pos.y += ny * growthBase;
      }
      if (effectiveCfg.ballGrowthAcceleration !== 0) ballGrowthRateRef.current += effectiveCfg.ballGrowthAcceleration;
  }

  private static clampVelocity(b: Ball, config: SimulationConfig) {
      const sp = Math.sqrt(b.vel.x * b.vel.x + b.vel.y * b.vel.y);
      if (config.mode !== 'SPACE_SPIRAL') {
          if (sp > config.maxVelocityLimit) { 
              b.vel.x = (b.vel.x / sp) * config.maxVelocityLimit; b.vel.y = (b.vel.y / sp) * config.maxVelocityLimit; 
          } else if (sp < config.minVelocityLimit && sp > 0.01) { 
              const boost = config.minVelocityLimit * (config.antiStallMultiplier || 1.0);
              b.vel.x = (b.vel.x / sp) * boost; b.vel.y = (b.vel.y / sp) * boost; 
          }
      }
  }
}
